import React from "react"
import { SortState, TableColumn, SelectionModel, ActionItem } from "./types"
import { useTableState } from "./useTableState"
import { ColumnHeaderMenu } from "./subcomponents/ColumnHeaderMenu"
import { RowActionsMenu } from "./subcomponents/RowActionsMenu"
import { TableCheckbox } from "./subcomponents/TableCheckbox"
import { TablePager } from "./subcomponents/TablePager"

export interface TableProps<T> {
  data: T[]
  columns: TableColumn<T>[]
  // Row identity — required when selection is used. Defaults to String(row.id).
  getRowId?: (row: T) => string

  // --- Sort (controlled when onSortChange supplied, else uncontrolled) ---
  sort?: SortState
  onSortChange?: (next: SortState) => void
  defaultSort?: (a: T, b: T) => number

  // --- Filter (controlled: parent owns the value) ---
  activeFilters?: Record<string, string | null>
  onFilterChange?: (columnKey: string, value: string | null) => void

  // --- Search (uncontrolled input, client-side compute) ---
  searchTerm?: string
  onFilteredCountChange?: (count: number) => void

  // --- Pagination (server mode when pageCount supplied, else client mode when itemsPerPage supplied) ---
  itemsPerPage?: number
  page?: number
  pageCount?: number
  onPageChange?: (page: number) => void
  pager?: "windowed" | "none"

  // --- Selection & row actions ---
  selection?: SelectionModel<T>
  rowActions?: (row: T) => ActionItem[]

  // --- Presentation ---
  card?: boolean
  loading?: boolean
  emptyMessage?: React.ReactNode
  className?: string
}

const defaultGetRowId = (row: unknown): string => String((row as { id?: unknown }).id)

export const Table = <T,>({
  data,
  columns,
  getRowId = defaultGetRowId,
  sort,
  onSortChange,
  defaultSort,
  activeFilters,
  onFilterChange,
  searchTerm,
  onFilteredCountChange,
  itemsPerPage,
  page,
  pageCount,
  onPageChange,
  pager = "windowed",
  selection,
  rowActions,
  card = true,
  loading = false,
  emptyMessage = "No results found.",
  className = "wb-clients__table",
}: TableProps<T>) => {
  const { rows, filteredCount, sortState, currentPage, totalPages, handleSort, handleClearSort, setPage } =
    useTableState<T>({
      data,
      columns,
      sort,
      onSortChange,
      defaultSort,
      searchTerm,
      itemsPerPage,
      page,
      pageCount,
      onPageChange,
    })

  React.useEffect(() => {
    onFilteredCountChange?.(filteredCount)
  }, [filteredCount, onFilteredCountChange])

  // Only selectable rows participate in select-all: including non-selectable
  // rows (which can never enter selectedIds) would make allSelected permanently
  // false and hand onToggleAll ids the caller can't act on.
  const selectableRows = selection?.isSelectable ? rows.filter(selection.isSelectable) : rows
  const visibleIds = selection ? selectableRows.map(getRowId) : []
  const allSelected =
    !!selection && visibleIds.length > 0 && visibleIds.every((id) => selection.selectedIds.has(id))

  const colCount = columns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0)

  const sortIdentity = (col: TableColumn<T>) => col.sortKey ?? col.key

  const paginationActive = pageCount != null || itemsPerPage != null
  const showPager = pager !== "none" && paginationActive

  const table = (
    <table className={className}>
      <thead>
        <tr>
          {selection && (
            <th style={{ width: 40 }}>
              <TableCheckbox
                ariaLabel="Select all"
                checked={allSelected}
                onChange={() => selection.onToggleAll(visibleIds)}
              />
            </th>
          )}
          {columns.map((col) => (
            <ColumnHeaderMenu
              key={col.key}
              label={col.label ?? col.key}
              align={col.align}
              sortable={col.sortable}
              active={sortState?.key === sortIdentity(col)}
              dir={sortState?.key === sortIdentity(col) ? sortState?.dir : undefined}
              onSort={col.sortable ? (dir) => handleSort(sortIdentity(col), dir) : undefined}
              onClearSort={handleClearSort}
              filterable={col.filterable}
              filterOptions={col.filterOptions}
              activeFilter={activeFilters?.[col.key] ?? null}
              onFilter={onFilterChange ? (value) => onFilterChange(col.key, value) : undefined}
              filterContent={col.filterContent}
            />
          ))}
          {rowActions && <th aria-label="Actions" />}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={colCount}>Loading…</td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={colCount}>{emptyMessage}</td>
          </tr>
        ) : (
          rows.map((row) => {
            const id = getRowId(row)
            const selectable = selection?.isSelectable ? selection.isSelectable(row) : true
            return (
              <tr key={id}>
                {selection && (
                  <td>
                    <TableCheckbox
                      ariaLabel={`Select row ${id}`}
                      checked={selection.selectedIds.has(id)}
                      disabled={!selectable}
                      onChange={() => selection.onToggle(id)}
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} style={col.align === "right" ? { textAlign: "right" } : undefined}>
                    {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? "")}
                  </td>
                ))}
                {rowActions && (
                  <td className="fsp-admin__row-actions">
                    <RowActionsMenu items={rowActions(row)} />
                  </td>
                )}
              </tr>
            )
          })
        )}
      </tbody>
    </table>
  )

  return (
    <>
      {card ? <div className="wb-clients__card">{table}</div> : table}
      {showPager && (
        <div className="fsp-admin__table-foot">
          <TablePager page={currentPage} pages={totalPages} onPage={setPage} />
        </div>
      )}
    </>
  )
}
